import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { DIAL } from "../lib/dial";

const PRIORITY = ["MY", "SG", "ID", "BN", "TH", "PH"];
const flag = (r) => String.fromCodePoint(...[...r].map((c) => 127397 + c.charCodeAt(0)));
const names = new Intl.DisplayNames(["en"], { type: "region" });

const useCountries = () => useMemo(() => {
  const all = DIAL.map(([r, c]) => ({ r, c, n: names.of(r) || r }));
  const top = PRIORITY.map((p) => all.find((x) => x.r === p)).filter(Boolean);
  const rest = all.filter((x) => !PRIORITY.includes(x.r)).sort((a, b) => a.n.localeCompare(b.n));
  return [top, rest];
}, []);

export const dialOf = (region) => (DIAL.find(([r]) => r === region) || ["MY", 60])[1];

export const CountryPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [top, rest] = useCountries();
  const item = (x) => (
    <CommandItem key={x.r} value={`${x.n} +${x.c} ${x.r}`} onSelect={() => { onChange(x.r); setOpen(false); }} data-testid={`country-option-${x.r}`}>
      <span className="mr-2">{flag(x.r)}</span>{x.n} <span className="ml-1 muted">+{x.c}</span>
      {value === x.r && <Check className="ml-auto h-4 w-4" />}
    </CommandItem>
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-11 w-36 shrink-0 items-center justify-between rounded-md border border-[var(--line)] bg-white px-3 text-sm" data-testid="country-picker">
          <span>{flag(value)} +{dialOf(value)}</span><ChevronsUpDown className="h-4 w-4 muted" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search country or code…" data-testid="country-search" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup heading="Popular">{top.map(item)}</CommandGroup>
            <CommandGroup heading="All countries">{rest.map(item)}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
